// i18n
import '../locales/i18n';
// highlight
import '../utils/highlight';

// scroll bar
import 'simplebar/src/simplebar.css';

// lightbox
import 'react-image-lightbox/style.css';

// editor
import 'react-quill/dist/quill.snow.css';

// slick-carousel
import 'slick-carousel/slick/slick.css';
import 'slick-carousel/slick/slick-theme.css';

// lazy image
import 'react-lazy-load-image-component/src/effects/blur.css';
import 'react-lazy-load-image-component/src/effects/opacity.css';
import 'react-lazy-load-image-component/src/effects/black-and-white.css';

// fullcalendar
import '@fullcalendar/common/main.min.css';
import '@fullcalendar/daygrid/main.min.css';

import cookie from 'cookie';
import { ReactElement, ReactNode, useEffect } from 'react';
// next
import { NextPage } from 'next';
import Head from 'next/head';
import App, { AppProps, AppContext } from 'next/app';
//
import { Provider as ReduxProvider } from 'react-redux';
import { PersistGate } from 'redux-persist/lib/integration/react';
// @mui
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns';
// redux
import { store, persistor } from '../redux/store';
// utils
import { getSettings } from '../utils/settings';
import { SettingsValueProps } from '../components/settings/type';
// contexts
import { SettingsProvider } from '../contexts/SettingsContext';
import { CollapseDrawerProvider } from '../contexts/CollapseDrawerContext';
import { AuthProvider } from '~/contexts/DeviseTokenAuthContext';
import { LoanTypesProvider } from '~/contexts/LoanTypesContext';
import { PermissionProvider } from '~/contexts/PermissionContext';
import { PreviewsProvider } from '~/contexts/PreviewsContext';
import { WarningsProvider } from '~/contexts/WarningsContext';
import QueryProvider from '../contexts/QueryProvider';
import { LoanOfficersMapProvider } from '~/contexts/LoanOfficersMapContext';
import { AgentsMapProvider } from '~/contexts/AgentsMapContext';
// theme
import ThemeProvider from '../theme';
// components
import { ChartStyle } from '../components/chart';
import RtlLayout from '../components/RtlLayout';
import ProgressBar from '../components/ProgressBar';
import ThemeColorPresets from '../components/ThemeColorPresets';
import NotistackProvider from '../components/NotistackProvider';
import ThemeLocalization from '../components/ThemeLocalization';
import MotionLazyContainer from '../components/animate/MotionLazyContainer';
import Warnings from '~/components/warnings/Warnings';
// guards
import AuthGuard from '~/guards/AuthGuard';
// global styles
import '../../public/styles/index.css';
import { ListsDialogsProvider } from '~/contexts/ListsDialogsContext';
import {
  AddToListDialog,
  CreateOrEditListDialog as CreateListDialog,
  EditListDialog,
} from '~/components/lists';
import { VerificationRequestDialogProvider } from '~/contexts/VerificationRequestDialogContext';
import { useRouter } from 'next/router';
import { ALLOWED_PATHS, LOCAL_STORAGE_PARAMS_VERSION, NO_SCROLL_PATHS } from '~/config';
import ReviewCreditsDialog from '~/components/opportunities/review-credits-dialog';
import InsufficientCreditsDialog from '~/components/opportunities/insufficient-credits-dialog';

type NextPageWithLayout = NextPage & {
  getLayout?: (page: ReactElement) => ReactNode;
};

interface MyAppProps extends AppProps {
  settings: SettingsValueProps;
  Component: NextPageWithLayout;
}

export default function MyApp(props: MyAppProps) {
  const { Component, pageProps, settings } = props;
  const getLayout = Component.getLayout ?? ((page) => page);

  const { pathname } = useRouter();

  useEffect(() => {
    const currentVersion = localStorage.getItem('params:version');

    if (currentVersion !== LOCAL_STORAGE_PARAMS_VERSION) {
      localStorage.clear();
      localStorage.setItem('params:version', LOCAL_STORAGE_PARAMS_VERSION);
    }
  }, []);

  useEffect(() => {
    if (NO_SCROLL_PATHS.includes(pathname))
      document.querySelector('body')?.classList.add('no-scroll-body');

    return () => {
      document.querySelector('body')?.classList.remove('no-scroll-body');
    };
  }, [pathname]);

  return (
    <>
      <Head>
        <meta name="viewport" content="initial-scale=1, width=device-width" />
      </Head>

      <ReduxProvider store={store}>
        <PersistGate loading={null} persistor={persistor}>
          <LocalizationProvider dateAdapter={AdapterDateFns}>
            <ThemeProvider>
              <QueryProvider>
                <AuthProvider>
                  {ALLOWED_PATHS.includes(pathname) ? (
                    getLayout(<Component {...pageProps} />)
                  ) : (
                    <AuthGuard>
                      <CollapseDrawerProvider>
                        <SettingsProvider defaultSettings={settings}>
                          <NotistackProvider>
                            <VerificationRequestDialogProvider>
                              <PermissionProvider>
                                <PreviewsProvider>
                                  <WarningsProvider>
                                    <ListsDialogsProvider>
                                      <LoanTypesProvider>
                                        <LoanOfficersMapProvider>
                                          <AgentsMapProvider>
                                            <MotionLazyContainer>
                                              <ThemeColorPresets>
                                                <ThemeLocalization>
                                                  <RtlLayout>
                                                    <ChartStyle />
                                                    <ProgressBar />
                                                    <Warnings />
                                                    <AddToListDialog />
                                                    <CreateListDialog />
                                                    <EditListDialog />
                                                    <ReviewCreditsDialog />
                                                    <InsufficientCreditsDialog />
                                                    {getLayout(<Component {...pageProps} />)}
                                                  </RtlLayout>
                                                </ThemeLocalization>
                                              </ThemeColorPresets>
                                            </MotionLazyContainer>
                                          </AgentsMapProvider>
                                        </LoanOfficersMapProvider>
                                      </LoanTypesProvider>
                                    </ListsDialogsProvider>
                                  </WarningsProvider>
                                </PreviewsProvider>
                              </PermissionProvider>
                            </VerificationRequestDialogProvider>
                          </NotistackProvider>
                        </SettingsProvider>
                      </CollapseDrawerProvider>
                    </AuthGuard>
                  )}
                </AuthProvider>
              </QueryProvider>
            </ThemeProvider>
          </LocalizationProvider>
        </PersistGate>
      </ReduxProvider>
    </>
  );
}

MyApp.getInitialProps = async (context: AppContext) => {
  const appProps = await App.getInitialProps(context);

  const cookies = cookie.parse(
    context.ctx.req ? context.ctx.req.headers.cookie || '' : document.cookie
  );

  const settings = getSettings(cookies);

  return {
    ...appProps,
    settings,
  };
};
